import { QuestionnaireResponseItem } from '@medplum/fhirtypes'
import { AdapterOptions, FlatAnswerMutation } from 'components/Assessments/adapters/types'
import { BaseSubsectionAdapter } from '../../../../../adapters/BaseSubsectionAdapter'
import { RecertFormField } from 'components/Assessments/RecertAssessment/types/RecertFormField'
import { RecertAssessmentQuestionnaireItem } from 'components/Assessments/RecertAssessment/types/questionnaire'
import { getAnswerValuesFromItems } from 'components/Assessments/helpers'
import { RecertFormData } from 'components/Assessments/RecertAssessment/types/RecertFormData'

/**
 * Respiratory subsection adapter
 * Handles respiratory assessment, cough, oxygen, and lung sounds (5 fields)
 */
export class RespiratoryAdapter extends BaseSubsectionAdapter<RecertFormData> {
  extractFormData(
    flatAnswers: Record<string, QuestionnaireResponseItem>,
    _options?: AdapterOptions
  ): Partial<RecertFormData> {
    const flatAnswersArray = Object.values(flatAnswers)

    // Respiratory assessment fields
    const respiratoryStatus = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RESPIRATORY_RESPIRATORY_WNL
    )?.at(0)

    const cough = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RESPIRATORY_COUGH
    )?.at(0)

    const oxygen = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RESPIRATORY_OXYGEN
    )?.at(0)

    const lungSounds = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RESPIRATORY_LUNG_SOUNDS
    )

    const systemSummary = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RESPIRATORY_SYSTEM_SUMMARY
    )?.at(0)

    return {
      [RecertFormField.RESPIRATORY_STATUS]: respiratoryStatus,
      [RecertFormField.RESPIRATORY_COUGH]: cough,
      [RecertFormField.RESPIRATORY_OXYGEN]: oxygen,
      [RecertFormField.RESPIRATORY_LUNG_SOUNDS]: lungSounds,
      [RecertFormField.RESPIRATORY_SYSTEM_SUMMARY]: systemSummary
    }
  }

  buildFlatAnswers(): FlatAnswerMutation[] {
    const mutations: FlatAnswerMutation[] = []

    if (this.formDataFieldExists(RecertFormField.RESPIRATORY_STATUS)) {
      mutations.push({
        linkId: RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RESPIRATORY_RESPIRATORY_WNL,
        value: this.getFormDataField(RecertFormField.RESPIRATORY_STATUS)
      })
    }

    if (this.formDataFieldExists(RecertFormField.RESPIRATORY_COUGH)) {
      mutations.push({
        linkId: RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RESPIRATORY_COUGH,
        value: this.getFormDataField(RecertFormField.RESPIRATORY_COUGH)
      })
    }

    if (this.formDataFieldExists(RecertFormField.RESPIRATORY_OXYGEN)) {
      mutations.push({
        linkId: RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RESPIRATORY_OXYGEN,
        value: this.getFormDataField(RecertFormField.RESPIRATORY_OXYGEN)
      })
    }

    if (this.formDataFieldExists(RecertFormField.RESPIRATORY_LUNG_SOUNDS)) {
      mutations.push({
        linkId: RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RESPIRATORY_LUNG_SOUNDS,
        value: this.getFormDataField(RecertFormField.RESPIRATORY_LUNG_SOUNDS)
      })
    }

    if (this.formDataFieldExists(RecertFormField.RESPIRATORY_SYSTEM_SUMMARY)) {
      mutations.push({
        linkId: RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RESPIRATORY_SYSTEM_SUMMARY,
        value: this.getFormDataField(RecertFormField.RESPIRATORY_SYSTEM_SUMMARY)
      })
    }

    return mutations
  }
}
